import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';
import { Check, Radar, RefreshCw, Receipt, Sparkles, StickyNote, X } from 'lucide-react';
import apiService from '../../services/apiService';
import NumericInput from './NumericInput';
import ForecastContextNotes from './ForecastContextNotes';
import ForecastQuarterlyGrowthDetail from './ForecastQuarterlyGrowthDetail';
import SeasonalityControl from './SeasonalityControl';
import { DEFAULT_SEASONALITY } from './forecastSeasonality';
import {
  ADJUSTMENT_YEARS, YEAR_LEVER_DEFS, FEE_DRIVER_DEFS, TREND_CATEGORIES
} from './forecastAdjustments';
import { summarizeQuarterlyGrowthProfile } from './forecastQuarterlyGrowth';
import { FORECAST_YEARS } from './forecastProjection';

const GOLD = '#FFAC03';
const MUTED = 'var(--ace-chart-2, #6b7c93)';
const GREEN = '#2f9e6e';

const CRIT_TONE = { high: 'red', medium: 'yellow', low: 'neutral' };
const PURPLE = '#9C6ADE';
const aiDraftSessionCache = new Map();
const reviewedSignalSessionCache = new Map();
const FIRST_FORECAST_YEAR = FORECAST_YEARS[0];
const LEVER_TABLE_HEADERS = [
  { id: 'lever', label: 'Lever' },
  {
    id: `fy${FIRST_FORECAST_YEAR - 3}`,
    label: `FY${FIRST_FORECAST_YEAR - 3}A`,
    className: 'hist'
  },
  {
    id: `fy${FIRST_FORECAST_YEAR - 2}`,
    label: `FY${FIRST_FORECAST_YEAR - 2}A`,
    className: 'hist'
  },
  {
    id: 'ytd',
    label: `${FIRST_FORECAST_YEAR - 1} YTD ann.`,
    className: 'hist'
  },
  { id: 'all-years', label: 'All years', className: 'parent-col' },
  ...ADJUSTMENT_YEARS.map((year) => ({ id: `fy${year}`, label: `FY${year}` }))
];
const LEVER_TABLE_COLUMN_COUNT = LEVER_TABLE_HEADERS.length;

const METHODOLOGY_LABELS = {
  primary_driver: 'Primary driver',
  scenario_mgmt: 'Scenario-driven mgmt assumption',
  driver_plus_mgmt: 'Driver × managed rate',
  mgmt_assumption: 'Management assumption'
};

const describeMacroCaseUsed = (macroCaseUsed) => {
  if (!macroCaseUsed) return null;
  if (macroCaseUsed.source === 'fallback-default') return 'Macro: fallback defaults';
  if (macroCaseUsed.source === 'central-settings') {
    return `Macro: ${macroCaseUsed.case || 'Base'} · central settings`;
  }
  return `Macro: ${macroCaseUsed.case || 'Base'}`;
};

// Map a GL group / rollup to the OP_03Y lever that an accepted AI forecast sets.
const leverForGroup = (glGroup, rollup) => {
  const exact = YEAR_LEVER_DEFS.find((def) => def.glGroup === glGroup);
  if (exact && rollup !== 'Balance Sheet') return exact;
  if (rollup === 'Balance Sheet') {
    if (glGroup === 'Loans') return YEAR_LEVER_DEFS.find((def) => def.id === 'loanGrowthPts');
    return YEAR_LEVER_DEFS.find((def) => def.id === 'depositGrowthPts');
  }
  if (rollup === 'Non-Interest Income') return YEAR_LEVER_DEFS.find((def) => def.id === 'feeAdjMM');
  if (rollup === 'Non-Interest Expense') {
    return ['Personnel Compensation', 'Employee Benefits'].includes(glGroup)
      ? YEAR_LEVER_DEFS.find((def) => def.id === 'compAdjMM')
      : YEAR_LEVER_DEFS.find((def) => def.id === 'expenseAdjMM');
  }
  if (rollup === 'Provision for Credit Losses') return YEAR_LEVER_DEFS.find((def) => def.id === 'provisionAdjMM');
  return null; // Interest income/expense: apply through balance growth levers instead.
};

// Which comparison GL groups roll up into a given OP_03 trend category.
const categoryMatchesGroup = (category, group) => {
  if (category.niiComposite) return ['Interest Income', 'Interest Expense'].includes(group.rollup);
  if (category.netComposite) return group.rollup !== 'Balance Sheet';
  if (category.glGroups) return category.glGroups.includes(group.glGroup);
  return group.rollup === category.rollup;
};

// Sub-rollup drill: one GL line's own actuals / prior-cycle plan / budget straight
// from the comparison data (the model only forecasts at the major-rollup level).
const buildSubRollupSeries = (group) => {
  if (!group) return [];
  const byPeriod = {};
  ['actuals', 'prior', 'budget'].forEach((seriesName) => {
    (group.series?.[seriesName] || []).forEach((point) => {
      const row = byPeriod[point.period] = byPeriod[point.period] || { x: point.period };
      row[seriesName] = (row[seriesName] || 0) + point.value / 1e6;
    });
  });
  return Object.values(byPeriod).sort((a, b) => a.x.localeCompare(b.x));
};

// Build the merged time axis: history months (actuals/prior) then forecast months.
const buildCategorySeries = (category, glGroups, baselineMonthly, adjustedMonthly) => {
  const history = {};
  const matches = (group) => categoryMatchesGroup(category, group);
  glGroups.filter(matches).forEach((group) => {
    const sign = (category.niiComposite && group.rollup === 'Interest Expense') ? -1 : 1;
    ['actuals', 'prior'].forEach((seriesName) => {
      (group.series[seriesName] || []).forEach((point) => {
        const row = history[point.period] = history[point.period] || { x: point.period };
        if (category.netComposite) {
          const expenseSide = ['Interest Expense', 'Non-Interest Expense', 'Provision for Credit Losses', 'Tax Expense'].includes(group.rollup);
          row[seriesName] = (row[seriesName] || 0) + (expenseSide ? -1 : 1) * point.value / 1e6;
        } else {
          row[seriesName] = (row[seriesName] || 0) + sign * point.value / 1e6;
        }
      });
    });
  });
  // Merge forecast rows into the same period map so the axis stays continuous
  // (history periods and forecast periods share one sorted timeline).
  baselineMonthly.forEach((row, index) => {
    const year = FORECAST_YEARS[0] + Math.floor(index / 12);
    const month = String((index % 12) + 1).padStart(2, '0');
    const x = `${year}-${month}`;
    const merged = history[x] = history[x] || { x };
    merged.baseline = row[category.modelField];
    merged.adjusted = adjustedMonthly[index] ? adjustedMonthly[index][category.modelField] : null;
  });
  // Bridge the hand-off: anchor both forecast lines at the last actual point so
  // actual trend, starting baseline, and adjusted plan connect visually.
  const actualPeriods = Object.values(history)
    .filter((row) => row.actuals !== undefined)
    .map((row) => row.x)
    .sort();
  const lastActual = actualPeriods[actualPeriods.length - 1];
  if (lastActual && history[lastActual]) {
    history[lastActual].baseline = history[lastActual].actuals;
    history[lastActual].adjusted = history[lastActual].actuals;
  }
  return Object.values(history).sort((a, b) => a.x.localeCompare(b.x));
};

const ForecastOp03Suite = ({
  planningEntity, cycleCode, division, market, author, baselineMonthly, adjustedMonthly,
  yearAdjustments, onLeverChange, onLeverSetAll, quarterlyGrowth, onQuarterlyGrowthChange,
  remainderYear, latestActualMonth, feeDrivers, onDriverChange, disabled, onAttentionCount,
  quarterlyDetailEnabled = true, macroCase = 'Base'
}) => {
  const [glData, setGlData] = useState(null);
  const [comparisonState, setComparisonState] = useState({ status: 'idle', message: '' });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [category, setCategory] = useState(TREND_CATEGORIES[2]); // NII default
  const [subRollup, setSubRollup] = useState(null); // OP_03L drill: null = All sub-rollups
  const [leverGroup, setLeverGroup] = useState('Balance sheet');
  const [insights, setInsights] = useState(null);
  const [scanning, setScanning] = useState(false);
  // Reviewed radar signals (glGroup -> true). Reviewing a signal — opening its
  // evidence, applying an AI forecast, or explicitly marking it — clears it from
  // the stage's attention badge. Signals inform review; they never block submit.
  // Cached at module scope (like aiDrafts) so the reviewed state survives stage
  // switches — otherwise the auto-scan on re-entry would resurrect the badge.
  const draftCacheKey = `${cycleCode || 'cycle'}:${planningEntity || 'aggregate'}`;
  const [aiDrafts, setAiDrafts] = useState(() => aiDraftSessionCache.get(draftCacheKey) || {}); // glGroup -> {status, data, guidance}
  const [reviewedSignals, setReviewedSignals] = useState(() => reviewedSignalSessionCache.get(draftCacheKey) || {});
  const markSignalReviewed = useCallback((glGroup, reviewed = true) => {
    setReviewedSignals((previous) => ({ ...previous, [glGroup]: reviewed }));
  }, []);
  const [modifyOpen, setModifyOpen] = useState(false);
  // OP_03R radar triggers render the AI draft inside this (normally collapsed)
  // drawer; make it controllable so we can auto-open and scroll the result in.
  const [trendDrawerOpen, setTrendDrawerOpen] = useState(false);
  const trendDrawerRef = useRef(null);
  const aiDraftAnchorRef = useRef(null);
  const autoScanFired = useRef(false);
  // Item C: anomaly-adjusted core history + transaction drill-through
  const [coreByGroup, setCoreByGroup] = useState({}); // glGroup -> core_history result
  const [useCore, setUseCore] = useState(false);      // forecast off the cleaned core basis
  const [seasonality, setSeasonality] = useState(DEFAULT_SEASONALITY); // Item E: intra-year shape
  const [hoverPeriod, setHoverPeriod] = useState(null);
  const [drill, setDrill] = useState(null);           // {status, period, data, error}
  const [commentDraft, setCommentDraft] = useState('');
  const [commentState, setCommentState] = useState('idle');
  const [quarterlyOpen, setQuarterlyOpen] = useState({});
  const [quarterlyPeriod, setQuarterlyPeriod] = useState({});
  const quarterlyProfileStatuses = useMemo(() => Object.fromEntries(
    ['loanGrowthPts', 'depositGrowthPts'].map((leverId) => [
      leverId,
      summarizeQuarterlyGrowthProfile({
        configs: quarterlyGrowth?.[leverId],
        annualTargets: yearAdjustments?.[leverId] || [],
        remainderYear,
        latestActualMonth
      })
    ])
  ), [latestActualMonth, quarterlyGrowth, remainderYear, yearAdjustments]);
  const quarterlyIssueCount = Object.values(quarterlyProfileStatuses)
    .reduce((sum, profile) => sum + profile.issueCount, 0);

  useEffect(() => {
    const count = (insights?.findings || []).filter(
      (finding) => finding.llmVerdict !== 'false_positive'
        && !reviewedSignals[finding.glGroup]
        && aiDrafts[finding.glGroup]?.status !== 'accepted'
    ).length + quarterlyIssueCount;
    onAttentionCount?.(count);
  }, [aiDrafts, insights, onAttentionCount, quarterlyIssueCount, reviewedSignals]);

  useEffect(() => {
    setAiDrafts(aiDraftSessionCache.get(draftCacheKey) || {});
    setReviewedSignals(reviewedSignalSessionCache.get(draftCacheKey) || {});
  }, [draftCacheKey]);

  useEffect(() => {
    aiDraftSessionCache.set(draftCacheKey, aiDrafts);
  }, [aiDrafts, draftCacheKey]);

  useEffect(() => {
    reviewedSignalSessionCache.set(draftCacheKey, reviewedSignals);
  }, [draftCacheKey, reviewedSignals]);

  const loadComparison = useCallback(() => {
    if (!planningEntity) return undefined;
    let stale = false;
    setComparisonState({ status: 'loading', message: '' });
    apiService.fetchForecastComparison({
      cycleCode, level: 'entity', entity: planningEntity, grain: 'monthly', detail: 'glGroup'
    })
      .then((data) => {
        if (!stale) {
          setGlData(data);
          setComparisonState({ status: 'ready', message: '' });
        }
      })
      .catch((error) => {
        if (!stale) {
          setComparisonState({
            status: 'error',
            message: error?.response?.data?.error || error?.message || 'The comparison data could not be loaded.'
          });
        }
      });
    return () => { stale = true; };
  }, [planningEntity, cycleCode]);

  useEffect(() => loadComparison(), [loadComparison]);

  const groups = useMemo(() => {
    const list = (glData?.groups || []).filter((g) => g.rollup !== 'Balance Sheet');
    return list
      .map((group) => ({
        ...group,
        recent: (group.series.actuals || []).slice(-3).reduce((s, p) => s + Math.abs(p.value), 0)
      }))
      .sort((a, b) => b.recent - a.recent);
  }, [glData]);

  // Resolve the selected group from the full GL set (incl. Balance Sheet, which
  // `groups` filters out) so off-trend radar findings like Loans/Deposits bind
  // their AI draft correctly instead of falling back to groups[0].
  const activeGroup = useMemo(
    () => groups.find((g) => g.glGroup === selectedGroup)
      || (glData?.groups || []).find((g) => g.glGroup === selectedGroup)
      || groups[0] || null,
    [groups, glData, selectedGroup]
  );
  const activeCore = activeGroup ? coreByGroup[activeGroup.glGroup] : null;

  // Item C: load anomaly-adjusted core history for the selected GL group.
  useEffect(() => {
    if (!planningEntity || !activeGroup) return undefined;
    const gl = activeGroup.glGroup;
    if (coreByGroup[gl]) return undefined;
    let stale = false;
    apiService.fetchCoreHistory({ cycleCode, entity: planningEntity, glGroup: gl })
      .then((data) => { if (!stale) setCoreByGroup((prev) => ({ ...prev, [gl]: data })); })
      .catch((error) => {
        if (!stale) setCoreByGroup((prev) => ({ ...prev, [gl]: { error: error?.response?.data?.error || 'unavailable' } }));
      });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningEntity, cycleCode, activeGroup && activeGroup.glGroup]);

  // Item C: open the transaction drill-through for one actual cell (period).
  const openDrill = (period) => {
    if (!period || !activeGroup || !planningEntity) return;
    setCommentDraft('');
    setCommentState('idle');
    setDrill({ status: 'loading', period });
    apiService.fetchTransactions({ entity: planningEntity, glGroup: activeGroup.glGroup, period })
      .then((data) => setDrill({ status: 'ready', period, data }))
      .catch((error) => setDrill({ status: 'error', period, error: error?.response?.data?.error || error.message }));
  };

  // Drill-through commentary reuses the durable context-note store (keyed to the
  // entity + GL group + period), so analyst notes live alongside the AI's context.
  const saveDrillComment = () => {
    if (!commentDraft.trim() || !drill?.period || !activeGroup) return;
    setCommentState('saving');
    apiService.createContextNote({
      author, actingAs: author,
      cycleCode, planningEntity, division, market,
      glGroup: activeGroup.glGroup, category: 'Drill-through', horizon: 'durable',
      expenseType: null,
      body: `[${drill.period} · ${activeGroup.glGroup}] ${commentDraft.trim()}`,
    })
      .then(() => { setCommentState('saved'); setCommentDraft(''); })
      .catch(() => setCommentState('error'));
  };

  const groupChart = useMemo(() => {
    if (!activeGroup) return [];
    const byPeriod = {};
    ['actuals', 'prior', 'budget'].forEach((name) => {
      (activeGroup.series[name] || []).forEach((point) => {
        const row = byPeriod[point.period] = byPeriod[point.period] || { x: point.period };
        row[name] = point.value / 1e6;
      });
    });

    // Item C: overlay the anomaly-adjusted "core history" (raw actuals minus
    // identified one-offs) — diverges from the actual line only at flagged months.
    const core = coreByGroup[activeGroup.glGroup];
    if (core && Array.isArray(core.coreSeries)) {
      core.coreSeries.forEach((point) => {
        const row = byPeriod[point.period] = byPeriod[point.period] || { x: point.period };
        row.core = point.valueMM;
      });
    }

    // Overlay the AI auto-forecast for the selected group, starting at the
    // first month after the last closed actual (stub year handled first).
    const draft = aiDrafts[activeGroup.glGroup];
    if (draft?.data && (draft.status === 'ready' || draft.status === 'accepted')) {
      const annual = [...(draft.data.annual || [])].sort((a, b) => a.year - b.year);
      const isBalance = draft.data.isBalance;
      const stub = draft.data.stubYear || {};
      const lastActualPeriod = draft.data.lastActualPeriod
        || (activeGroup.series.actuals || []).slice(-1)[0]?.period
        || '';
      const lastActualPoint = (activeGroup.series.actuals || []).slice(-1)[0];
      let previousValue = lastActualPoint ? lastActualPoint.value / 1e6 : (annual[0]?.valueMM || 0);
      // Item E: spread annual→monthly with the server-computed intra-year weights
      // (flows) / cumulative path (balances). Annual totals are preserved by
      // construction; fall back to flat/linear if no shape was returned.
      const seas = draft.data.seasonality || {};
      annual.forEach((entry) => {
        const isStub = entry.year === stub.year;
        const startMonth = isStub && lastActualPeriod
          ? Number(lastActualPeriod.slice(5, 7)) + 1
          : 1;
        const monthsInSpan = 12 - startMonth + 1;
        const flowRemainder = isStub
          ? Math.max(0, entry.valueMM - (stub.actualYtdMM || 0))
          : entry.valueMM;
        const weights = seas.weightsByYear?.[String(entry.year)];
        const cumulative = seas.cumulativeByYear?.[String(entry.year)];
        for (let month = startMonth; month <= 12; month += 1) {
          const period = `${entry.year}-${String(month).padStart(2, '0')}`;
          const row = byPeriod[period] = byPeriod[period] || { x: period };
          const k = month - startMonth;
          if (isBalance) {
            const frac = cumulative && cumulative[k] != null
              ? cumulative[k]
              : (month - startMonth + 1) / monthsInSpan;
            row.aiForecast = previousValue + (entry.valueMM - previousValue) * frac;
          } else {
            row.aiForecast = weights && weights[k] != null
              ? flowRemainder * weights[k]
              : flowRemainder / monthsInSpan;
          }
        }
        previousValue = entry.valueMM;
      });
      // Anchor the AI line to the last actual point for visual continuity.
      if (lastActualPeriod && byPeriod[lastActualPeriod] && lastActualPoint) {
        byPeriod[lastActualPeriod].aiForecast = lastActualPoint.value / 1e6;
      }
    }
    return Object.values(byPeriod).sort((a, b) => a.x.localeCompare(b.x));
  }, [activeGroup, aiDrafts, coreByGroup]);

  // OP_03L sub-rollups available under the active major category, derived from the
  // comparison GL set; the selected one (if any) drives the drilled chart.
  const subRollupOptions = useMemo(() => {
    const names = [];
    (glData?.groups || []).forEach((group) => {
      if (categoryMatchesGroup(category, group) && !names.includes(group.glGroup)) {
        names.push(group.glGroup);
      }
    });
    return names;
  }, [glData, category]);
  const subRollupGroup = useMemo(
    () => (subRollup ? (glData?.groups || []).find((g) => g.glGroup === subRollup) : null),
    [subRollup, glData]
  );
  useEffect(() => {
    if (subRollup && !subRollupOptions.includes(subRollup)) setSubRollup(null);
  }, [subRollup, subRollupOptions]);
  const categoryChart = useMemo(
    () => (subRollup && subRollupGroup
      ? buildSubRollupSeries(subRollupGroup)
      : buildCategorySeries(category, glData?.groups || [], baselineMonthly, adjustedMonthly)),
    [subRollup, subRollupGroup, glData, category, baselineMonthly, adjustedMonthly]
  );

  // The history window rolls with the first forecast year; balances use last
  // EOP while flow values use annual sums or annualized YTD values.
  const historyByGroup = useMemo(() => {
    const lookup = {};
    (glData?.groups || []).forEach((group) => {
      const isBalance = group.measure === 'EOP';
      const byYear = {};
      (group.series.actuals || []).forEach((point) => {
        const year = point.period.slice(0, 4);
        const bucket = byYear[year] = byYear[year] || { sum: 0, months: 0, last: 0, lastPeriod: '' };
        bucket.sum += point.value;
        bucket.months += 1;
        if (point.period > bucket.lastPeriod) {
          bucket.lastPeriod = point.period;
          bucket.last = point.value;
        }
      });
      const annual = (year) => {
        const bucket = byYear[year];
        if (!bucket) return null;
        if (isBalance) return bucket.last / 1e6;
        return (bucket.sum / 1e6);
      };
      const ytd = byYear[String(FIRST_FORECAST_YEAR - 1)];
      lookup[group.glGroup] = {
        isBalance,
        firstHistoricalActual: annual(String(FIRST_FORECAST_YEAR - 3)),
        secondHistoricalActual: annual(String(FIRST_FORECAST_YEAR - 2)),
        ytdAnn: ytd ? (isBalance ? ytd.last / 1e6 : (ytd.sum / ytd.months) * 12 / 1e6) : null
      };
    });
    return lookup;
  }, [glData]);

  const formatHistory = (history) => {
    if (!history || history === undefined) return '—';
    return history === null ? '—' : `$${history.toFixed(1)}`;
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const result = await apiService.fetchForecastInsights({ cycleCode, entity: planningEntity });
      setInsights(result);
    } catch (error) {
      setInsights({ error: error?.response?.data?.error || error.message });
    } finally {
      setScanning(false);
    }
  };

  // Auto-run the off-trend scan once the Supabase actuals/baseline load resolves.
  useEffect(() => {
    if (glData && planningEntity && !autoScanFired.current) {
      autoScanFired.current = true;
      runScan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glData, planningEntity]);

  // Reveal the (collapsible) trend-review drawer and bring a target into view.
  // The drawer must open before its content is laid out, so defer the scroll.
  const revealTrendReview = (targetRef) => {
    setTrendDrawerOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      (targetRef?.current || trendDrawerRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
  };

  const runAutoForecast = async (glGroup, guidance = '') => {
    setSelectedGroup(glGroup);
    revealTrendReview(aiDraftAnchorRef);
    setModifyOpen(false);
    setAiDrafts((previous) => ({ ...previous, [glGroup]: { status: 'loading', guidance } }));
    try {
      const data = await apiService.fetchAutoForecast({
        cycleCode, entity: planningEntity, glGroup, division, market,
        guidance: guidance || undefined,
        basis: useCore ? 'core' : 'actual',
        macroCase,
        seasonality // Item E: server applies this intra-year shape, totals preserved
      });
      setAiDrafts((previous) => ({ ...previous, [glGroup]: { status: 'ready', data, guidance } }));
    } catch (error) {
      setAiDrafts((previous) => ({
        ...previous,
        [glGroup]: { status: 'error', message: error?.response?.data?.error || error.message }
      }));
    }
  };

  const activeDraft = activeGroup ? aiDrafts[activeGroup.glGroup] : null;
  const activeMacroCaseLabel = describeMacroCaseUsed(activeDraft?.data?.macroCaseUsed);

  const acceptDraft = () => {
    const draft = activeDraft?.data;
    if (!draft || disabled) return;
    const lever = leverForGroup(draft.glGroup, draft.rollup);
    if (!lever) return;
    const deltaByYear = Object.fromEntries(
      (draft.adjustmentsVsPriorPlan || []).map((entry) => [entry.year, entry.valueMM])
    );
    if (lever.unit === 'pts') {
      // Balances: convert annual EOP paths to growth-rate deltas vs prior plan.
      const aiByYear = Object.fromEntries((draft.annual || []).map((e) => [e.year, e.valueMM]));
      const planByYear = Object.fromEntries(
        Object.entries(draft.priorPlanAnnual || {}).map(([y, v]) => [Number(y), v])
      );
      const lastActual = (activeGroup?.series?.actuals || []).slice(-1)[0];
      const base = lastActual ? lastActual.value / 1e6 : null;
      ADJUSTMENT_YEARS.forEach((year, yearIndex) => {
        const aiPrev = aiByYear[year - 1] ?? base;
        const planPrev = planByYear[year - 1] ?? base;
        if (!aiPrev || !planPrev || !aiByYear[year] || !planByYear[year]) return;
        const aiGrowth = (aiByYear[year] / aiPrev - 1) * 100;
        const planGrowth = (planByYear[year] / planPrev - 1) * 100;
        const pts = Math.max(lever.min, Math.min(lever.max, Number((aiGrowth - planGrowth).toFixed(2))));
        onLeverChange(lever, yearIndex, pts, 'AI Auto Forecast');
      });
    } else {
      ADJUSTMENT_YEARS.forEach((year, yearIndex) => {
        if (deltaByYear[year] === undefined) return;
        const value = Math.max(lever.min, Math.min(lever.max, Number(deltaByYear[year].toFixed(1))));
        onLeverChange(lever, yearIndex, value, 'AI Auto Forecast');
      });
    }
    setAiDrafts((previous) => ({
      ...previous,
      [draft.glGroup]: { ...previous[draft.glGroup], status: 'accepted' }
    }));
  };

  return (
    <div className="op3-suite">
      {comparisonState.status === 'error' && (
        <div className="forecast-panel forecast-returned-banner" role="alert">
          <div>
            <strong>Trend comparison unavailable</strong>
            <p>Your lever inputs are preserved. Evidence and off-trend results may be stale until the comparison reloads.</p>
            <small>{comparisonState.message}</small>
          </div>
          <button type="button" className="forecast-secondary-button compact" onClick={loadComparison}>Retry comparison</button>
        </div>
      )}
      {/* ---- Trend review by GL group (entity sessions only) ---- */}
      {planningEntity && (
      <details
        className="forecast-subpanel workspace-change-drawer"
        ref={trendDrawerRef}
        open={trendDrawerOpen}
        onToggle={(event) => setTrendDrawerOpen(event.currentTarget.open)}
      >
        <summary>Detailed GL trend, source evidence, and context notes</summary>
        <div className="workspace-change-content">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">OP_03T · Trend review by GL group</span>
          <h3>Actuals vs prior-cycle forecast — find what is off-trend before touching a lever</h3>
        </div>
        <div className="op3-chips">
          {groups.slice(0, 14).map((group) => (
            <button
              key={group.glGroup} type="button"
              className={`forecast-health-chip clickable ${activeGroup?.glGroup === group.glGroup ? 'gold' : 'neutral'}`}
              onClick={() => setSelectedGroup(group.glGroup)}
            >
              {group.glGroup}
            </button>
          ))}
        </div>
        <div
          style={{ width: '100%', height: 220 }}
          onContextMenu={(e) => { e.preventDefault(); openDrill(hoverPeriod); }}
          title="Right-click a month to drill into its transactions"
        >
          <ResponsiveContainer>
            <ComposedChart
              data={groupChart}
              onMouseMove={(s) => { if (s && s.activeLabel) setHoverPeriod(s.activeLabel); }}
            >
              <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
              <XAxis dataKey="x" minTickGap={40} />
              <YAxis tickFormatter={(v) => `$${v.toFixed(1)}`} width={64} domain={['auto', 'auto']} />
              <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(2)}MM`, n]} />
              <Legend />
              <Line dataKey="prior" name="Prior forecast" stroke={MUTED} strokeDasharray="5 3" dot={false} strokeWidth={1.6} />
              <Line dataKey="budget" name="Budget" stroke={GREEN} strokeDasharray="2 3" dot={false} strokeWidth={1.2} />
              <Line dataKey="actuals" name="Actuals" stroke={GOLD} dot={false} strokeWidth={2.2} />
              <Line dataKey="core" name="Core history (ex one-offs)" stroke={GOLD} strokeDasharray="2 2" dot={false} strokeWidth={1.5} connectNulls />
              <Line dataKey="aiForecast" name="AI auto-forecast" stroke={PURPLE} strokeWidth={2.2} strokeDasharray="7 3" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* ---- Item E: per-line AI Auto Forecast + intra-year shape control ---- */}
        {activeGroup && (
          <div className="op3-seasonality-panel">
            <div className="forecast-submit-chips op3-seasonality-run">
              <span className="forecast-health-chip neutral">
                <Sparkles size={11} style={{ marginRight: 4 }} />
                AI Auto Forecast · {activeGroup.glGroup}
              </span>
              <button
                type="button"
                className="forecast-home-action ai-forecast-button"
                disabled={activeDraft?.status === 'loading'}
                onClick={() => runAutoForecast(activeGroup.glGroup, activeDraft?.guidance || '')}
              >
                <Sparkles size={12} style={{ marginRight: 4 }} />
                {activeDraft?.status === 'loading' ? 'Forecasting…' : 'Run AI Auto Forecast'}
              </button>
            </div>
            <SeasonalityControl
              value={seasonality}
              onChange={setSeasonality}
              note="Applies to this line’s AI Auto Forecast. The annual total is held; only the within-year shape changes."
            />
          </div>
        )}

        {/* ---- Item C: anomaly-adjusted core history + drill controls ---- */}
        {activeCore && !activeCore.error && (
          <div className="op3-core-panel">
            <div className="forecast-submit-chips">
              <span className="forecast-health-chip" style={{ borderColor: GOLD, background: 'color-mix(in srgb, #FFAC03 12%, transparent)' }}>
                {activeCore.hasCore ? `core history excludes $${activeCore.excludedTotalMM}MM one-offs` : 'core history = actuals (no one-offs found)'}
              </span>
              <span className="forecast-health-chip neutral" title="Variance is measured against a seasonal-naive expected, so normal seasonal peaks are not flagged">
                top-{activeCore.topN} variance · seasonality-aware
              </span>
            </div>
            {(activeCore.flagged || []).map((f) => (
              <div key={f.period} className={`op3-flag ${f.judgment === 'one_time' ? 'one-time' : ''}`}>
                <button
                  type="button"
                  className={`forecast-health-chip clickable ${f.judgment === 'one_time' ? 'gold' : 'neutral'}`}
                  onClick={() => openDrill(f.period)}
                  title="View the transactions behind this month"
                >
                  <Receipt size={11} style={{ marginRight: 4, verticalAlign: '-1px' }} />
                  {f.period}{f.excludedMM ? ` · −$${f.excludedMM}MM` : ''}
                </button>
                <span className="op3-flag-reason">{f.reason}</span>
              </div>
            ))}
            <div className="op3-core-controls">
              <label className="op3-core-toggle">
                <input type="checkbox" checked={useCore} onChange={(e) => setUseCore(e.target.checked)} />
                Forecast off core history (exclude one-offs)
              </label>
              <span className="forecast-submit-note" style={{ margin: 0 }}>
                Right-click any month on the chart — or pick one — to see its transactions:
              </span>
              <select
                className="op3-period-select" defaultValue=""
                onChange={(e) => { if (e.target.value) { openDrill(e.target.value); e.target.value = ''; } }}
                aria-label="Open transaction detail for a month"
              >
                <option value="" disabled>month…</option>
                {groupChart.filter((r) => r.actuals !== undefined).map((r) => (
                  <option key={r.x} value={r.x}>{r.x}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ---- Item C: transaction drill-through ---- */}
        {drill && (
          <div className="op3-drill">
            <div className="op3-drill-head">
              <strong>
                <Receipt size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                Transactions · {activeGroup?.glGroup} · {drill.period}
              </strong>
              <button type="button" className="forecast-secondary-button compact" onClick={() => setDrill(null)}>
                <X size={12} /> Close
              </button>
            </div>
            {drill.status === 'loading' && <p className="forecast-submit-note">Loading transaction detail…</p>}
            {drill.status === 'error' && <p className="forecast-submit-note">{drill.error}</p>}
            {drill.status === 'ready' && drill.data && (
              <>
                <div className="forecast-submit-chips">
                  <span className="forecast-health-chip neutral">total ${(drill.data.total / 1e6).toFixed(2)}MM</span>
                  {drill.data.oneOffTotal ? (
                    <span className="forecast-health-chip gold">one-off ${(drill.data.oneOffTotal / 1e6).toFixed(2)}MM</span>
                  ) : null}
                  <span className="forecast-health-chip neutral">{drill.data.count} transactions</span>
                </div>
                {drill.data.count === 0 && (
                  <p className="forecast-submit-note">No transaction detail seeded for this cell.</p>
                )}
                {drill.data.count > 0 && (
                  <table className="op3-drill-table">
                    <thead>
                      <tr><th>Posting</th><th>Party</th><th>Source</th><th>Type</th><th>Memo</th><th style={{ textAlign: 'right' }}>$MM</th></tr>
                    </thead>
                    <tbody>
                      {drill.data.rows.map((r) => (
                        <tr key={r.transactionId} className={!r.isRecurring ? 'one-time' : (r.capitalized ? 'capitalized' : '')}>
                          <td>{r.postingDate}</td>
                          <td>{r.vendor || r.employee || r.counterparty || '—'}</td>
                          <td>{r.sourceSystem}</td>
                          <td>{r.dc ? `${r.txnType} · ${r.dc}` : r.txnType}{r.capitalized ? ` · cap ${r.benefitMonths || ''}mo` : ''}</td>
                          <td className="op3-drill-memo">{r.memo}</td>
                          <td style={{ textAlign: 'right' }}>{(r.amount / 1e6).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {drill.data.page?.hasMore && (
                  <p className="forecast-submit-note">Showing the largest {drill.data.rows.length} of {drill.data.count}.</p>
                )}
                <div className="op3-drill-comment">
                  <StickyNote size={12} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                  <input
                    type="text" name="gl-cell-commentary" autoComplete="off" className="op3-note"
                    aria-label="Analyst commentary for the selected GL period"
                    placeholder="Add analyst commentary for this cell…"
                    value={commentDraft} onChange={(e) => setCommentDraft(e.target.value)} disabled={disabled}
                  />
                  <button
                    type="button" className="forecast-primary-button compact" onClick={saveDrillComment}
                    disabled={disabled || !commentDraft.trim() || commentState === 'saving'}
                  >
                    {commentState === 'saving' ? 'Saving…' : commentState === 'saved' ? 'Saved ✓' : 'Save note'}
                  </button>
                </div>
                {commentState === 'error' && <p className="forecast-submit-note">Could not save the note.</p>}
              </>
            )}
          </div>
        )}

        <div ref={aiDraftAnchorRef} aria-hidden="true" />
        {activeDraft?.status === 'loading' && (
          <p className="forecast-submit-note">
            <Sparkles size={12} style={{ marginRight: 5, verticalAlign: '-2px' }} />
            Sonnet is building a driver-based forecast for {activeGroup?.glGroup}… (extended thinking, ~15–30s)
          </p>
        )}
        {activeDraft?.status === 'error' && (
          <p className="forecast-submit-note">Auto-forecast failed: {activeDraft.message}</p>
        )}
        {(activeDraft?.status === 'ready' || activeDraft?.status === 'accepted') && activeDraft.data && (
          <div className="ai-draft-panel">
            <div className="forecast-submit-chips">
              <span className="forecast-health-chip" style={{ borderColor: PURPLE, background: 'color-mix(in srgb, #9C6ADE 14%, transparent)' }}>
                <Sparkles size={11} style={{ marginRight: 4 }} />
                {activeDraft.data.generator === 'sonnet-thinking' ? 'claude-sonnet-4-6 · thinking' : 'Statistical fallback'}
              </span>
              <span className="forecast-health-chip neutral">{METHODOLOGY_LABELS[activeDraft.data.methodologyBucket]}</span>
              <span className="forecast-health-chip neutral">confidence: {activeDraft.data.confidence ?? 'Not scored'}</span>
              {activeMacroCaseLabel && <span className="forecast-health-chip neutral">{activeMacroCaseLabel}</span>}
              {activeDraft.data.review && activeDraft.data.review.verdict !== 'skipped' && (
                <span
                  className={`forecast-health-chip ${activeDraft.data.review.verdict === 'approve' ? 'green' : 'yellow'}`}
                  title={(activeDraft.data.review.checks || [])
                    .map((check) => `${check.passed ? '✓' : '✗'} ${check.name}: ${check.note}`)
                    .join('\n') || activeDraft.data.review.critique}
                >
                  review: {activeDraft.data.review.verdict === 'approve' ? 'approved' : 'open issues'}
                  {activeDraft.data.revisions ? ` · revised ×${activeDraft.data.revisions}` : ''}
                </span>
              )}
              {activeDraft.data.backtest?.mape6mPct !== null && activeDraft.data.backtest?.mape6mPct !== undefined && (
                <span className="forecast-health-chip neutral" title={activeDraft.data.backtest.method}>
                  backtest MAPE {activeDraft.data.backtest.mape6mPct}%
                </span>
              )}
              {activeDraft.data.startPeriod && (
                <span className="forecast-health-chip neutral">starts {activeDraft.data.startPeriod}</span>
              )}
              {activeDraft.data.seasonality?.applied && (
                <span className="forecast-health-chip neutral" title={activeDraft.data.seasonality.applied.note}>
                  shape: {activeDraft.data.seasonality.applied.label}
                </span>
              )}
              {activeDraft.data.clipped && (
                <span className="forecast-health-chip yellow">clipped to reasonability bounds</span>
              )}
              {activeDraft.status === 'accepted' && (
                <span className="forecast-health-chip green">applied to OP_03Y levers</span>
              )}
            </div>
            <p className="ai-draft-method"><strong>{activeDraft.data.method}</strong></p>
            <div className="forecast-submit-chips">
              {(activeDraft.data.drivers || []).map((driver) => (
                <span className="forecast-health-chip neutral" key={driver.name} title={driver.rationale}>
                  {driver.name}: {driver.value}
                  {driver.linkedTo ? <em className="ai-driver-link"> ↳ {driver.linkedTo}</em> : null}
                </span>
              ))}
            </div>
            <p className="forecast-submit-note">{activeDraft.data.narrative}</p>
            {activeDraft.data.contextNotes?.count > 0 && (
              <div className="forecast-submit-chips ai-context-cites">
                <span className="forecast-health-chip" style={{ borderColor: PURPLE, background: 'color-mix(in srgb, #9C6ADE 12%, transparent)' }}>
                  <Sparkles size={11} style={{ marginRight: 4 }} />
                  read {activeDraft.data.contextNotes.count} context note{activeDraft.data.contextNotes.count === 1 ? '' : 's'}
                </span>
                {(activeDraft.data.contextNotes.loaded || [])
                  .filter((ref) => (activeDraft.data.contextNotes.cited || []).includes(ref.ref))
                  .map((ref) => (
                    <span className="forecast-health-chip moss" key={ref.ref} title={ref.excerpt}>
                      cited {ref.ref}: {ref.scope}
                    </span>
                  ))}
                {(activeDraft.data.contextNotes.cited || []).length === 0 && (
                  <span className="forecast-health-chip neutral">none cited as decisive</span>
                )}
              </div>
            )}
            <div className="forecast-home-table-wrap">
              <table className="forecast-home-table">
                <thead>
                  <tr>
                    <th />
                    {(activeDraft.data.annual || []).map((entry) => (
                      <th key={entry.year}>
                        FY{entry.year}{entry.year === activeDraft.data.stubYear?.year ? ' (stub)' : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>AI forecast ($MM)</strong></td>
                    {(activeDraft.data.annual || []).map((entry) => (
                      <td key={entry.year} className="forecast-mono">{entry.valueMM.toFixed(1)}</td>
                    ))}
                  </tr>
                  <tr>
                    <td>Δ vs prior plan</td>
                    {(activeDraft.data.annual || []).map((entry) => {
                      const delta = (activeDraft.data.adjustmentsVsPriorPlan || [])
                        .find((d) => d.year === entry.year);
                      return (
                        <td key={entry.year} className={`forecast-mono ${delta && delta.valueMM >= 0 ? 'pos' : 'neg'}`}>
                          {delta ? `${delta.valueMM >= 0 ? '+' : ''}${delta.valueMM.toFixed(1)}` : '—'}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            {activeDraft.status === 'ready' && !disabled && (
              <div className="forecast-submit-chips" style={{ marginTop: 8 }}>
                {leverForGroup(activeDraft.data.glGroup, activeDraft.data.rollup) ? (
                  <button type="button" className="forecast-primary-button compact" onClick={acceptDraft}>
                    <Check size={13} /> Accept → set {leverForGroup(activeDraft.data.glGroup, activeDraft.data.rollup).label}
                  </button>
                ) : (
                  <span className="forecast-health-chip yellow">
                    NII line — apply through the balance growth levers instead
                  </span>
                )}
                <button
                  type="button" className="forecast-secondary-button compact"
                  onClick={() => setAiDrafts((previous) => ({
                    ...previous,
                    [activeDraft.data.glGroup]: { ...previous[activeDraft.data.glGroup], status: 'rejected' }
                  }))}
                >
                  <X size={13} /> Reject
                </button>
                <button type="button" className="forecast-secondary-button compact" onClick={() => setModifyOpen((open) => !open)}>
                  Prompt to modify
                </button>
              </div>
            )}
            {modifyOpen && activeDraft.status === 'ready' && (
              <div className="ai-modify-stack">
              <SeasonalityControl
                value={seasonality}
                onChange={setSeasonality}
                compact
                note="Pick the intra-year shape, then Regenerate to re-spread this forecast."
              />
              <div className="ai-modify-row">
                <input
                  type="text"
                  name="ai-forecast-guidance"
                  autoComplete="off"
                  aria-label="Guidance for regenerating the AI forecast"
                  className="op3-note"
                  placeholder="Guidance, e.g. cap fee growth at 3%…"
                  defaultValue={activeDraft.guidance || ''}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') runAutoForecast(activeDraft.data.glGroup, event.target.value);
                  }}
                />
                <button
                  type="button" className="forecast-secondary-button compact"
                  onClick={(event) => {
                    const input = event.currentTarget.previousSibling;
                    runAutoForecast(activeDraft.data.glGroup, input.value);
                  }}
                >
                  Regenerate
                </button>
              </div>
              </div>
            )}
          </div>
        )}

        <div className="op3-context-notes">
          <ForecastContextNotes
            compact
            scope={{ planningEntity, division, market, cycleCode }}
            author={author}
            glGroup={activeGroup?.glGroup || ''}
            disabled={disabled}
          />
        </div>
        </div>
      </details>

      )}

      {/* ---- Off-trend radar (entity sessions only) ---- */}
      {planningEntity && (
      <div className="forecast-subpanel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">OP_03R · Off-trend radar</span>
          <h3>
            <Radar size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Statistical scan, criticality = % off-trend × dollar impact{insights?.llm?.status === 'reviewed' ? ' · Haiku-reviewed' : ''}
          </h3>
        </div>
        <div className="forecast-submit-chips" style={{ marginBottom: 10 }}>
          <button type="button" className="forecast-home-action" onClick={runScan} disabled={scanning}>
            <RefreshCw size={13} style={{ marginRight: 5 }} />
            {scanning ? 'Scanning…' : insights ? 'Re-run scan' : 'Run off-trend scan'}
          </button>
          {insights?.llm && (
            <span className="forecast-health-chip neutral">
              guardrail: {insights.llm.status}{insights.llm.model ? ` (${insights.llm.model.split('-').slice(0, 3).join('-')})` : ''}
            </span>
          )}
        </div>
        {insights?.error && <p className="forecast-submit-note">{insights.error}</p>}
        {insights?.findings && (
          <div className="forecast-home-table-wrap">
            <table className="forecast-home-table op3-findings">
              <thead>
                <tr><th>Criticality</th><th>GL line</th><th>% off-trend</th><th>$ impact (ann.)</th><th>Read</th><th /></tr>
              </thead>
              <tbody>
                {insights.findings.filter((finding) => finding.llmVerdict !== 'false_positive').map((finding) => {
                  const isReviewed = Boolean(reviewedSignals[finding.glGroup])
                    || aiDrafts[finding.glGroup]?.status === 'accepted';
                  return (
                  <tr key={finding.glGroup} style={isReviewed ? { opacity: 0.55 } : undefined}>
                    <td>
                      <span className={`forecast-health-chip ${CRIT_TONE[finding.criticality]}`}>{finding.criticality}</span>
                      {finding.llmVerdict && finding.llmVerdict !== 'confirmed' && (
                        <span className="forecast-health-chip neutral" title={finding.llmNote || ''}>{finding.llmVerdict}</span>
                      )}
                      {isReviewed && (
                        <span className="forecast-health-chip moss">reviewed</span>
                      )}
                    </td>
                    <td><strong>{finding.glGroup}</strong></td>
                    <td className={finding.direction === 'favorable' ? 'pos' : 'neg'}>
                      {finding.pctDeviation > 0 ? '+' : ''}{finding.pctDeviation.toFixed(1)}%
                    </td>
                    <td className={finding.direction === 'favorable' ? 'pos' : 'neg'}>
                      {finding.dollarImpactAnnual >= 0 ? '+' : '-'}${Math.abs(finding.dollarImpactAnnual / 1e6).toFixed(2)}MM
                    </td>
                    <td className="op3-narrative">{finding.narrative}{finding.llmNote ? ` — ${finding.llmNote}` : ''}</td>
                    <td>
                      <button
                        type="button"
                        className="forecast-secondary-button compact"
                        onClick={() => {
                          setSelectedGroup(finding.glGroup);
                          const cat = TREND_CATEGORIES.find((c) =>
                            c.rollup === finding.rollup || (c.glGroups || []).includes(finding.glGroup));
                          if (cat) setCategory(cat);
                          revealTrendReview();
                          markSignalReviewed(finding.glGroup);
                        }}
                      >
                        Review evidence
                      </button>
                      <button
                        type="button"
                        className="forecast-secondary-button compact"
                        aria-pressed={Boolean(reviewedSignals[finding.glGroup])}
                        onClick={() => markSignalReviewed(finding.glGroup, !reviewedSignals[finding.glGroup])}
                      >
                        {reviewedSignals[finding.glGroup] ? 'Reviewed ✓ · undo' : 'Mark reviewed'}
                      </button>
                      <button
                        type="button"
                        className="forecast-home-action ai-forecast-button"
                        disabled={aiDrafts[finding.glGroup]?.status === 'loading'}
                        onClick={() => runAutoForecast(finding.glGroup)}
                      >
                        <Sparkles size={12} style={{ marginRight: 4 }} />
                        {aiDrafts[finding.glGroup]?.status === 'loading'
                          ? 'Forecasting…'
                          : aiDrafts[finding.glGroup]?.status === 'accepted'
                          ? 'Applied ✓'
                          : 'AI Auto Forecast'}
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="forecast-submit-note">
              Signals flag lines worth a look — they never block submission. Reviewing evidence,
              applying an AI forecast, or marking a row reviewed clears it from the stage&apos;s
              attention badge.
            </p>
          </div>
        )}
      </div>

      )}

      {/* ---- Live three-line category chart ---- */}
      <div className="forecast-subpanel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">OP_03L · Live category trend</span>
          <h3>Actuals → starting baseline → your adjusted plan, one category at a time</h3>
        </div>
        <div className="op3-chips">
          {TREND_CATEGORIES.map((candidate) => (
            <button
              key={candidate.id} type="button"
              className={`forecast-health-chip clickable ${category.id === candidate.id ? 'gold' : 'neutral'}`}
              onClick={() => { setCategory(candidate); setSubRollup(null); }}
            >
              {candidate.label}
            </button>
          ))}
        </div>
        {subRollupOptions.length > 1 && (
          <div className="op3-chips op3-subchips" style={{ marginTop: 2 }}>
            <span className="forecast-home-market" style={{ alignSelf: 'center', marginRight: 4 }}>Sub-rollup:</span>
            <button
              type="button"
              className={`forecast-health-chip clickable ${subRollup === null ? 'gold' : 'neutral'}`}
              onClick={() => setSubRollup(null)}
            >
              All {category.label}
            </button>
            {subRollupOptions.map((name) => (
              <button
                key={name} type="button"
                className={`forecast-health-chip clickable ${subRollup === name ? 'gold' : 'neutral'}`}
                onClick={() => setSubRollup(name)}
              >
                {name}
              </button>
            ))}
          </div>
        )}
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <ComposedChart data={categoryChart}>
              <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
              <XAxis dataKey="x" minTickGap={48} />
              <YAxis tickFormatter={(v) => `$${Math.round(v)}`} width={64} domain={['auto', 'auto']} />
              <Tooltip formatter={(v, n) => [`$${Number(v).toFixed(2)}MM`, n]} />
              <Legend />
              <Line dataKey="actuals" name="Actual trend" stroke={GOLD} dot={false} strokeWidth={2.2} connectNulls />
              {subRollup ? (
                <>
                  <Line dataKey="prior" name="Prior-cycle plan" stroke={MUTED} strokeDasharray="5 3" dot={false} strokeWidth={1.6} connectNulls />
                  <Line dataKey="budget" name="Budget" stroke={GREEN} strokeDasharray="2 3" dot={false} strokeWidth={1.6} connectNulls />
                </>
              ) : (
                <>
                  <Line dataKey="baseline" name="Starting baseline" stroke={MUTED} strokeDasharray="5 3" dot={false} strokeWidth={1.6} connectNulls />
                  <Line dataKey="adjusted" name="Adjusted plan" stroke={GREEN} dot={false} strokeWidth={2} connectNulls />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="forecast-submit-note">
          {subRollup
            ? `${subRollup}: actual trend vs the prior-cycle plan and budget for this GL line. The live adjusted plan is modeled at the ${category.label} level — switch to “All” to see it move with your levers.`
            : 'History is the management-view actuals at this unit’s scale; forecast lines are the module model. Adjust any per-year lever or driver below and the green line moves immediately.'}
        </p>
      </div>

      {/* ---- Per-year adjustments ---- */}
      <div className="forecast-subpanel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">OP_03Y · Per-year adjustments</span>
          <h3>Every lever by fiscal year — steppers, not sliders</h3>
        </div>
        <div className="workspace-stage-tabs" role="group" aria-label="Business lever categories">
          {['Balance sheet', 'Non-interest revenue', 'Non-interest expense', 'Credit'].map((group) => (
            <button
              type="button" key={group}
              aria-pressed={leverGroup === group}
              className={`forecast-secondary-button compact ${leverGroup === group ? 'active' : ''}`}
              onClick={() => setLeverGroup(group)}
            >
              {group}
            </button>
          ))}
        </div>
        <div className="forecast-home-table-wrap">
          <table className="forecast-home-table op3-year-table aligned">
            <thead>
              <tr>
                {LEVER_TABLE_HEADERS.map((header) => (
                  <th key={header.id} className={header.className}>{header.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {YEAR_LEVER_DEFS.filter((def) => def.group === leverGroup).map((def) => {
                const history = historyByGroup[def.glGroup];
                const values = yearAdjustments[def.id];
                // Custom per-year values lock the parent input.
                const isCustom = values.some((value) => value !== values[0]);
                const supportsQuarterlyDetail = quarterlyDetailEnabled
                  && ['loanGrowthPts', 'depositGrowthPts'].includes(def.id);
                const isQuarterlyOpen = Boolean(quarterlyOpen[def.id]);
                const quarterlyStatus = quarterlyProfileStatuses[def.id];
                return (
                  <React.Fragment key={def.id}>
                  <tr>
                    <td>
                      <strong>{def.label}</strong>
                      <small className="forecast-home-market"> {def.unit}</small>
                      {supportsQuarterlyDetail ? (
                        <>
                        <button
                          type="button"
                          className="quarterly-detail-trigger"
                          aria-expanded={isQuarterlyOpen}
                          aria-controls={`quarterly-detail-${def.id}`}
                          onClick={() => setQuarterlyOpen((previous) => ({
                            ...previous,
                            [def.id]: !previous[def.id]
                          }))}
                        >
                          {isQuarterlyOpen ? 'Hide Quarterly Detail' : quarterlyStatus.configuredCount > 0
                            ? `Quarterly Detail · ${quarterlyStatus.configuredCount}`
                            : 'Add Quarterly Detail'}
                        </button>
                        {quarterlyStatus.issueCount > 0 ? (
                          <span className="quarterly-detail-warning">
                            Needs Attention · {quarterlyStatus.issueCount}
                          </span>
                        ) : null}
                        </>
                      ) : null}
                    </td>
                    <td className="forecast-mono hist">
                      {history ? formatHistory(history.firstHistoricalActual) : '—'}
                    </td>
                    <td className="forecast-mono hist">
                      {history ? formatHistory(history.secondHistoricalActual) : '—'}
                    </td>
                    <td className="forecast-mono hist">{history ? formatHistory(history.ytdAnn) : '—'}</td>
                    <td className="parent-col">
                      {isCustom ? (
                        <button
                          type="button"
                          className="lever-parent-locked"
                          title="Per-year custom values are set — click to clear them and unlock the all-years input"
                          disabled={disabled}
                          onClick={() => onLeverSetAll(def, 0)}
                        >
                          custom 🔒
                        </button>
                      ) : (
                        <NumericInput
                          ariaLabel={`${def.label} all years`}
                          value={values[0]}
                          step={def.step}
                          min={def.min}
                          max={def.max}
                          disabled={disabled}
                          title="Sets every fiscal year to this amount"
                          onCommit={(value) => onLeverSetAll(def, value)}
                        />
                      )}
                    </td>
                    {ADJUSTMENT_YEARS.map((year, yearIndex) => (
                      <td key={year}>
                        <NumericInput
                          ariaLabel={`${def.label} FY${year}`}
                          value={values[yearIndex]}
                          step={def.step}
                          min={def.min}
                          max={def.max}
                          disabled={disabled}
                          onCommit={(value) => onLeverChange(def, yearIndex, value)}
                        />
                        {supportsQuarterlyDetail
                          && quarterlyStatus.details[String(year)]?.configured
                          && quarterlyStatus.details[String(year)]?.preserveAnnual === false ? (
                            <small className="quarterly-effective-rate">
                              Effective {quarterlyStatus.details[String(year)].effectiveRate >= 0 ? '+' : ''}
                              {Number.isFinite(quarterlyStatus.details[String(year)].effectiveRate)
                                ? quarterlyStatus.details[String(year)].effectiveRate.toFixed(2)
                                : '—'} pts
                            </small>
                          ) : null}
                      </td>
                    ))}
                  </tr>
                  {supportsQuarterlyDetail && isQuarterlyOpen ? (
                    <tr className="quarterly-detail-row">
                      <td colSpan={LEVER_TABLE_COLUMN_COUNT}>
                        <ForecastQuarterlyGrowthDetail
                          def={def}
                          yearAdjustments={values}
                          quarterlyGrowth={quarterlyGrowth}
                          onChange={onQuarterlyGrowthChange}
                          remainderYear={remainderYear}
                          latestActualMonth={latestActualMonth}
                          selectedPeriod={quarterlyPeriod[def.id] || 'roy'}
                          onSelectedPeriodChange={(period) => setQuarterlyPeriod((previous) => ({
                            ...previous,
                            [def.id]: period
                          }))}
                          disabled={disabled}
                        />
                      </td>
                    </tr>
                  ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <p className="forecast-submit-note">
            History columns are the related GL line at this unit ($MM): balances show period-end,
            flows show full-year totals, 2026 shows the annualized year-to-date run-rate. Enter one
            amount under "All years" to apply it to every fiscal year; editing any single year locks
            the parent until you clear the custom values.
          </p>
        </div>

        {leverGroup === 'Non-interest revenue' && <>
        <h4 className="op3-driver-head">Driver-based non-interest revenue</h4>
        {FEE_DRIVER_DEFS.map((def) => {
          const state = feeDrivers.find((d) => d.id === def.id);
          if (!state) return null;
          return (
            <div className="op3-driver" key={def.id}>
              <div className="op3-driver-row">
                <strong>{def.label}</strong>
                <small className="forecast-home-market">{def.basisLabel} (bps)</small>
                {ADJUSTMENT_YEARS.map((year, yearIndex) => (
                  <label key={year} className="op3-driver-cell">
                    <span>FY{String(year).slice(2)}</span>
                    <NumericInput
                      ariaLabel={`${def.label} ratio FY${year}`}
                      step={5} min={-200} max={400}
                      value={state.ratioBps[yearIndex]}
                      disabled={disabled}
                      onCommit={(value) => {
                        const next = state.ratioBps.map((v, i) => (i === yearIndex ? value : v));
                        onDriverChange(def, { ...state, ratioBps: next }, `ratio FY${year}`);
                      }}
                    />
                  </label>
                ))}
              </div>
              <div className="op3-overlays">
                {state.overlays.map((overlay, index) => (
                  <div className="op3-overlay-row" key={index}>
                    <select
                      aria-label={`${def.label} overlay period`}
                      value={overlay.monthIndex} disabled={disabled}
                      onChange={(event) => {
                        const overlays = state.overlays.map((o, i) => (i === index ? { ...o, monthIndex: Number(event.target.value) } : o));
                        onDriverChange(def, { ...state, overlays }, 'overlay month');
                      }}
                    >
                      {Array.from({ length: 24 }, (_, m) => (
                        <option key={m} value={m}>{`FY${2027 + Math.floor(m / 12)} M${String((m % 12) + 1).padStart(2, '0')}`}</option>
                      ))}
                    </select>
                    <select
                      aria-label={`${def.label} overlay mode`}
                      value={overlay.mode} disabled={disabled}
                      onChange={(event) => {
                        const overlays = state.overlays.map((o, i) => (i === index ? { ...o, mode: event.target.value } : o));
                        onDriverChange(def, { ...state, overlays }, 'overlay mode');
                      }}
                    >
                      <option value="add">Add to driver</option>
                      <option value="replace">Replace driver</option>
                    </select>
                    <NumericInput
                      ariaLabel={`${def.label} overlay amount`}
                      className="op3-stepper wide" step={0.1} placeholder="$MM"
                      value={overlay.amountMM} disabled={disabled}
                      onCommit={(value) => {
                        const overlays = state.overlays.map((o, i) => (i === index ? { ...o, amountMM: value } : o));
                        onDriverChange(def, { ...state, overlays }, 'overlay amount');
                      }}
                    />
                    <input
                      type="text" name={`${def.id}-overlay-${index}-note`} autoComplete="off"
                      aria-label={`${def.label} overlay pipeline note`}
                      className="op3-note" placeholder="Pipeline note, e.g. known Q1 syndication fee…"
                      value={overlay.note || ''} disabled={disabled}
                      onChange={(event) => {
                        const overlays = state.overlays.map((o, i) => (i === index ? { ...o, note: event.target.value } : o));
                        onDriverChange(def, { ...state, overlays }, 'overlay note');
                      }}
                    />
                    {!disabled && (
                      <button
                        type="button" className="forecast-secondary-button compact"
                        aria-label={`Remove ${def.label} period overlay`}
                        onClick={() => onDriverChange(def, { ...state, overlays: state.overlays.filter((_, i) => i !== index) }, 'overlay removed')}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {!disabled && (
                  <button
                    type="button" className="forecast-secondary-button compact"
                    onClick={() => onDriverChange(def, {
                      ...state,
                      overlays: [...state.overlays, { monthIndex: 0, mode: 'add', amountMM: 0, note: '' }]
                    }, 'overlay added')}
                  >
                    + Period overlay
                  </button>
                )}
              </div>
            </div>
          );
        })}
        </>}
      </div>
    </div>
  );
};

export default ForecastOp03Suite;
